import { useCallback, useEffect, useRef, useState, type MouseEvent } from "react";
import { ExternalLink, FileText, Loader2 } from "lucide-react";
import { getMyHmHrSubordinates, getMyLetterStatus, updateUserLetterLink, uploadFile } from "../../services/usersapi";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import Expe from "../../letters/experiance-letter";
import OfferLetter from "../../letters/offer-letter-hr";
import HmContractLetter from "../../letters/offer-letter-hm";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { jsPDF } from "jspdf";
import { toast } from "react-toastify";
import html2canvas from "html2canvas";
import { useRbac } from "../../context";

interface SubordinateUser {
  id: string;
  username: string;
  email: string;
  phone: string;
  role: "HM" | "HR" | string;
  location?: string;
  isBlocked?: boolean;
  letters?: {
    offerLetter?: { refNo: string; url?: string | null } | null;
    experienceLetter?: { refNo: string; url?: string | null } | null;
  } | null;
  gender?: string | null;
  onboarding?: { gender?: string } | null;
}

interface MyLetterStatus {
  letters: {
    offerLetter: { refNo: string; url: string | null } | null;
    experienceLetter: { refNo: string; url: string | null } | null;
  };
}

function LetterAccessCardSkeleton() {
  return (
    <div className="w-full min-h-[150px] sm:min-h-[170px] rounded-2xl border border-gray-200 bg-gray-50 p-4 sm:p-5 animate-pulse">
      <div className="flex items-start justify-between gap-2 sm:gap-3">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-gray-200 shrink-0" />
          <div className="min-w-0">
            <div className="h-4 w-28 sm:w-36 rounded bg-gray-200" />
            <div className="mt-2 h-3 w-20 sm:w-28 rounded bg-gray-200" />
          </div>
        </div>
        <div className="h-6 w-16 rounded-full bg-gray-200 shrink-0" />
      </div>
      <div className="mt-4 h-3 w-28 rounded bg-gray-200" />
    </div>
  );
}

/** Supports both flat API bodies and occasional `{ data: { subordinates } }` nesting. */
function extractHmHrSubordinatesList(body: unknown): SubordinateUser[] {
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const d = root.data as Record<string, unknown> | undefined;
  if (d && Array.isArray(d.subordinates)) return d.subordinates as SubordinateUser[];
  if (Array.isArray(root.subordinates)) return root.subordinates as SubordinateUser[];
  const inner = d?.data as Record<string, unknown> | undefined;
  if (inner && Array.isArray(inner.subordinates)) return inner.subordinates as SubordinateUser[];
  return [];
}

function extractHmHrPagination(body: unknown): { totalPages: number; totalResults: number } {
  if (!body || typeof body !== "object") return { totalPages: 0, totalResults: 0 };
  const root = body as Record<string, unknown>;
  const d = root.data as Record<string, unknown> | undefined;
  const totalPages = Number(root.totalPages ?? d?.totalPages ?? 0) || 0;
  const totalResults = Number(root.totalResults ?? d?.totalResults ?? 0) || 0;
  return { totalPages, totalResults };
}

function LetterSquares({
  offerLetterUrl,
  experienceLetterUrl,
  verticalOnMobile = false,
  large = false,
}: {
  offerLetterUrl?: string | null;
  experienceLetterUrl?: string | null;
  verticalOnMobile?: boolean;
  large?: boolean;
}) {
  const handleOpenLetter = (event: MouseEvent<HTMLButtonElement>, url?: string | null, label?: string) => {
    event.stopPropagation();
    if (!url) {
      toast.info(`${label} is not available yet.`);
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className={`flex items-center gap-2 ${verticalOnMobile ? "flex-col sm:flex-row" : ""}`}>
      <button
        type="button"
        onClick={(event) => handleOpenLetter(event, offerLetterUrl, "Offer Letter")}
        className={`${large ? "h-32 w-32 text-base" : "h-16 w-16 text-[11px]"} rounded-md border border-gray-300 bg-white font-semibold leading-tight text-gray-700 hover:border-blue-400 hover:text-blue-700`}
      >
        Offer
        <br />
        Letter
      </button>
      <button
        type="button"
        onClick={(event) => handleOpenLetter(event, experienceLetterUrl, "Experience Letter")}
        className={`${large ? "h-32 w-32 text-base" : "h-16 w-16 text-[11px]"} rounded-md border border-gray-300 bg-white font-semibold leading-tight text-gray-700 hover:border-blue-400 hover:text-blue-700`}
      >
        Experience
        <br />
        Letter
      </button>
    </div>
  );
}

function LetterAccessCard({
  title,
  href,
}: {
  title: "Offer Letter" | "Experience Letter";
  href?: string | null;
}) {
  const isAvailable = Boolean(href);

  return (
    <button
      type="button"
      onClick={() => {
        if (!href) {
          toast.info(`${title} is not uploaded yet. Please contact admin.`);
          return;
        }
        window.open(href, "_blank", "noopener,noreferrer");
      }}
      className={`w-full min-h-[150px] sm:min-h-[170px] rounded-2xl border p-4 sm:p-5 text-left transition ${
        isAvailable
          ? "border-blue-200 bg-blue-50 hover:border-blue-400 hover:bg-blue-100"
          : "border-gray-200 bg-gray-50 hover:bg-gray-100"
      }`}
    >
      <div className="flex items-start justify-between gap-2 sm:gap-3">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <span
            className={`inline-flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-xl shrink-0 ${
              isAvailable ? "bg-blue-100 text-blue-700" : "bg-gray-200 text-gray-500"
            }`}
          >
            <FileText className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <p className="text-sm sm:text-base font-semibold text-gray-900 break-words">{title}</p>
            <p className="text-xs sm:text-sm text-gray-600">{isAvailable ? "Tap to open PDF" : "Not uploaded yet"}</p>
          </div>
        </div>
        <span
          className={`inline-flex items-center rounded-full px-2 py-1 text-[11px] sm:text-xs font-medium shrink-0 ${
            isAvailable ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-600"
          }`}
        >
          {isAvailable ? "Available" : "Pending"}
        </span>
      </div>
      {isAvailable ? (
        <div className="mt-4 inline-flex items-center text-xs sm:text-sm font-medium text-gray-700">
          Open document
          <ExternalLink className="ml-2 h-4 w-4" />
        </div>
      ) : null}
    </button>
  );
}

const OfferExperienceLetter = () => {
  const { user } = useRbac();
  const canCreateLetters = ["ADMIN", "SHM", "FBDE"].includes(user?.role || "");
  const isHmHrViewOnly = ["HM", "HR"].includes(user?.role || "");
  const [users, setUsers] = useState<SubordinateUser[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [selectedLetterType, setSelectedLetterType] = useState<"experience" | "joining">("experience");
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [docScale, setDocScale] = useState<number>(1);
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<"all" | "HM" | "HR" | "SHM">("all");
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(20);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalResults, setTotalResults] = useState<number>(0);
  const [isSubmittingLetter, setIsSubmittingLetter] = useState<boolean>(false);
  const [localPdfPreviewUrl, setLocalPdfPreviewUrl] = useState<string>("");
  const [isExperienceDateDialogOpen, setIsExperienceDateDialogOpen] = useState<boolean>(false);
  const [experienceFromDate, setExperienceFromDate] = useState<string>("");
  const [experienceToDate, setExperienceToDate] = useState<string>("");
  const [experienceDesignationInput, setExperienceDesignationInput] = useState<string>("");
  const [experienceFormErrors, setExperienceFormErrors] = useState<{
    fromDate?: string;
    toDate?: string;
    designation?: string;
  }>({});
  const [isJoiningDateDialogOpen, setIsJoiningDateDialogOpen] = useState<boolean>(false);
  const [joiningDateInput, setJoiningDateInput] = useState<string>("");
  const [hmContractAddress, setHmContractAddress] = useState<string>("");
  const [hmContractDateInput, setHmContractDateInput] = useState<string>("");
  const [myLetterStatus, setMyLetterStatus] = useState<MyLetterStatus>({
    letters: {
      offerLetter: null,
      experienceLetter: null,
    },
  });
  const [isLoadingMyLetterStatus, setIsLoadingMyLetterStatus] = useState<boolean>(false);
  const captureRootRef = useRef<HTMLDivElement | null>(null);

  const fetchUsers = useCallback(async () => {
    if (isHmHrViewOnly) {
      setUsers([]);
      setError("");
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError("");
      const response = await getMyHmHrSubordinates({
        page,
        limit,
        search: searchTerm || undefined,
        role: selectedRole,
      });
      const list = extractHmHrSubordinatesList(response);
      setUsers(list);
      const { totalPages: tp, totalResults: tr } = extractHmHrPagination(response);
      setTotalPages(tp);
      setTotalResults(tr);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Unable to load list.");
      setUsers([]);
      setTotalPages(0);
      setTotalResults(0);
    } finally {
      setIsLoading(false);
    }
  }, [isHmHrViewOnly, page, limit, searchTerm, selectedRole]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (!isHmHrViewOnly) return;
    const fetchMyLetterStatus = async () => {
      setIsLoadingMyLetterStatus(true);
      try {
        const response = await getMyLetterStatus();
        const currentUser = response?.data?.user || {};
        setMyLetterStatus({
          letters: {
            offerLetter:
              currentUser.letters?.offerLetter ||
              (currentUser.joiningLetterUrl
                ? {
                    refNo: currentUser.userId ? `${currentUser.userId}/OFFER` : "OFFER",
                    url: currentUser.joiningLetterUrl,
                  }
                : null),
            experienceLetter:
              currentUser.letters?.experienceLetter ||
              (currentUser.experienceLetterUrl
                ? {
                    refNo: currentUser.userId ? `${currentUser.userId}/EXP` : "EXP",
                    url: currentUser.experienceLetterUrl,
                  }
                : null),
          },
        });
      } catch {
        setMyLetterStatus({
          letters: {
            offerLetter: null,
            experienceLetter: null,
          },
        });
      } finally {
        setIsLoadingMyLetterStatus(false);
      }
    };
    fetchMyLetterStatus();
  }, [isHmHrViewOnly]);

  useEffect(() => {
    if (selectedUserId && !users.some((user) => user.id === selectedUserId)) {
      setSelectedUserId("");
    }
  }, [users, selectedUserId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setSearchTerm(searchInput.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const selectedUser = users.find((user) => user.id === selectedUserId);
  const activeUser = selectedUser ?? null;
  const letterGender =
    activeUser?.gender?.trim() ||
    activeUser?.onboarding?.gender?.trim() ||
    undefined;
  const today = new Date().toLocaleDateString("en-GB");
  const previewDesignation = activeUser?.role === "HR" ? "HR Recruiter Intern" : activeUser?.role;
  const isHmJoiningContract = selectedLetterType === "joining" && activeUser?.role === "HM";
  const existingLetterUrl =
    selectedLetterType === "experience"
      ? activeUser?.letters?.experienceLetter?.url
      : activeUser?.letters?.offerLetter?.url;
  const activeRefNo =
    selectedLetterType === "experience"
      ? activeUser?.letters?.experienceLetter?.refNo
      : activeUser?.letters?.offerLetter?.refNo;
  const effectivePreviewUrl = localPdfPreviewUrl || existingLetterUrl || "";

  useEffect(() => {
    setExperienceDesignationInput(previewDesignation || "");
  }, [previewDesignation, selectedUserId]);

  useEffect(() => {
    if (isHmJoiningContract) {
      setHmContractAddress(activeUser?.location || "");
    }
  }, [activeUser?.location, isHmJoiningContract, selectedUserId]);

  useEffect(() => {
    if (!isExperienceDateDialogOpen) {
      setExperienceFormErrors({});
    }
  }, [isExperienceDateDialogOpen]);

  const createLetterPdfBlob = async (): Promise<Blob> => {
    const root = captureRootRef.current;
    if (!root) throw new Error("Letter template is not ready.");
    const pageNodes = root.querySelectorAll<HTMLElement>("[data-capture-page]");
    if (!pageNodes.length) throw new Error("No printable pages found.");

    const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
    for (let i = 0; i < pageNodes.length; i += 1) {
      const pageNode = pageNodes[i];
      const canvas = await html2canvas(pageNode, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const imgData = canvas.toDataURL("image/png");
      if (i > 0) pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, 0, 210, 297, undefined, "FAST");
    }
    return pdf.output("blob");
  };

  const handleSubmitLetter = async (
    manualDates?: {
      fromDate: string;
      toDate: string;
      joiningDate?: string;
      address?: string;
      designation?: string;
      contractDate?: string;
    }
  ) => {
    if (!activeUser) return;
    try {
      setIsSubmittingLetter(true);
      if (selectedLetterType === "experience" && manualDates) {
        setExperienceFromDate(manualDates.fromDate);
        setExperienceToDate(manualDates.toDate);
        if (typeof manualDates.designation === "string") {
          setExperienceDesignationInput(manualDates.designation);
        }
        await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));
      } else if (selectedLetterType === "joining") {
        if (manualDates?.joiningDate) {
          setJoiningDateInput(manualDates.joiningDate);
        }
        if (typeof manualDates?.address === "string") {
          setHmContractAddress(manualDates.address);
        }
        if (typeof manualDates?.contractDate === "string") {
          setHmContractDateInput(manualDates.contractDate);
        }
        await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));
      }
      const blob = await createLetterPdfBlob();
      const localPreview = URL.createObjectURL(blob);
      setLocalPdfPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return localPreview;
      });
      const fileName = `${activeUser.username.replace(/\s+/g, "_")}_${selectedLetterType}_letter.pdf`;
      const file = new File([blob], fileName, { type: "application/pdf" });
      const uploadResponse = await uploadFile(file, `letters/${selectedLetterType}`);
      const fileUrl = uploadResponse?.fileUrl;
      if (!fileUrl) {
        throw new Error("Upload did not return a file URL.");
      }

      await updateUserLetterLink(activeUser.id, {
        letterType: selectedLetterType,
        letterUrl: fileUrl,
      });

      const fallbackRefNo =
        activeRefNo || `${activeUser.id}/${selectedLetterType === "experience" ? "EXP" : "OFFER"}`;

      setUsers((prev) =>
        prev.map((u) =>
          u.id === activeUser.id
            ? {
                ...u,
                letters: {
                  offerLetter:
                    selectedLetterType === "joining"
                      ? {
                          refNo: u.letters?.offerLetter?.refNo || fallbackRefNo,
                          url: fileUrl,
                        }
                      : (u.letters?.offerLetter ?? null),
                  experienceLetter:
                    selectedLetterType === "experience"
                      ? {
                          refNo: u.letters?.experienceLetter?.refNo || fallbackRefNo,
                          url: fileUrl,
                        }
                      : (u.letters?.experienceLetter ?? null),
                },
              }
            : u
        )
      );
      toast.success("Letter PDF generated and saved.");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to submit letter.");
    } finally {
      setIsSubmittingLetter(false);
    }
  };

  const handleSubmitClick = () => {
    if (!canCreateLetters || !activeUser || isSubmittingLetter) return;
    if (selectedLetterType === "experience") {
      setIsExperienceDateDialogOpen(true);
      return;
    }
    setIsJoiningDateDialogOpen(true);
  };

  useEffect(() => {
    setLocalPdfPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });
  }, [selectedUserId, selectedLetterType, isPreviewOpen]);

  useEffect(() => {
    return () => {
      if (localPdfPreviewUrl) {
        URL.revokeObjectURL(localPdfPreviewUrl);
      }
    };
  }, [localPdfPreviewUrl]);

  useEffect(() => {
    const recalcScale = () => {
      const availableWidth = window.innerWidth * 0.95 - 64;
      const availableHeight = window.innerHeight * 0.92 - 120;
      const widthScale = availableWidth / 794;
      const heightScale = availableHeight / 1123;
      const nextScale = Math.min(1, widthScale, heightScale);
      setDocScale(Math.max(0.35, nextScale));
    };

    if (isPreviewOpen) {
      recalcScale();
      window.addEventListener("resize", recalcScale);
    }
    return () => window.removeEventListener("resize", recalcScale);
  }, [isPreviewOpen]);

  return (
    <div className="p-4 md:p-6">
      {activeUser ? (
        <div
          ref={captureRootRef}
          style={{
            position: "fixed",
            left: "-99999px",
            top: "0",
            opacity: 0,
            pointerEvents: "none",
            zIndex: -1,
          }}
          aria-hidden="true"
        >
          {selectedLetterType === "experience" ? (
            <div data-capture-page style={{ width: 794, height: 1123, overflow: "hidden", background: "#fff" }}>
              <Expe
                date={today}
                refNo={activeUser?.letters?.experienceLetter?.refNo}
                candidateName={activeUser.username}
                designation={experienceDesignationInput || previewDesignation}
                fromDate={experienceFromDate || undefined}
                toDate={experienceToDate || undefined}
                gender={letterGender}
              />
            </div>
          ) : isHmJoiningContract ? (
            <>
              {[1, 2, 3, 4, 5, 6,7].map((pageNumber) => (
                <div
                  key={`capture-hm-contract-page-${pageNumber}`}
                  data-capture-page
                  style={{ width: 794, height: 1123, overflow: "hidden", background: "#fff" }}
                >
                  <HmContractLetter
                    date={hmContractDateInput || undefined}
                    candidateName={activeUser.username}
                    address={hmContractAddress || activeUser.location || undefined}
                    pageNumber={pageNumber}
                    gender={letterGender}
                  />
                </div>
              ))}
            </>
          ) : (
            <>
              {[1, 2, 3, 4, 5].map((pageNumber) => (
                <div
                  key={`capture-joining-page-${pageNumber}`}
                  data-capture-page
                  style={{ width: 794, height: 1123, overflow: "hidden", background: "#fff" }}
                >
                  <OfferLetter
                    date={today}
                    refNo={activeUser?.letters?.offerLetter?.refNo}
                    candidateName={activeUser.username}
                    designation={previewDesignation}
                    joiningDate={joiningDateInput || today}
                    pageNumber={pageNumber}
                    gender={letterGender}
                  />
                </div>
              ))}
            </>
          )}
        </div>
      ) : null}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 md:p-6">
        <div className="mb-4">
          <h1 className="text-xl md:text-2xl font-semibold text-gray-900">Offer/Experience Letter</h1>
        </div>
        
        {!isHmHrViewOnly && (
          <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name, email, or phone..."
              className="h-10"
            />
            <select
              value={selectedRole}
              onChange={(e) => {
                setPage(1);
                setSelectedRole(e.target.value as "all" | "HM" | "HR" | "SHM");
              }}
              className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm"
            >
              <option value="all">All Roles</option>
              <option value="HM">HM</option>
              <option value="HR">HR</option>
              <option value="SHM">SHM</option>
            </select>
            <select
              value={String(limit)}
              onChange={(e) => {
                setPage(1);
                setLimit(Number(e.target.value));
              }}
              className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm"
            >
              <option value="10">10 per page</option>
              <option value="20">20 per page</option>
              <option value="50">50 per page</option>
              <option value="100">100 per page</option>
            </select>
          </div>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-10 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading list...
          </div>
        )}

        {!isLoading && error && !isHmHrViewOnly && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        {!isLoading && isHmHrViewOnly && (
          <div className="border border-gray-200 rounded-xl p-4 sm:p-6 min-h-[calc(100vh-220px)] sm:min-h-[calc(100vh-220px)] flex flex-col items-center justify-center">
            <div className="w-full max-w-3xl">
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 text-center">Your Documents</h2>
              <p className="mt-2 text-sm text-gray-600 text-center">Tap a card to open the PDF.</p>
              <div className="mt-6 sm:mt-8 grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                {isLoadingMyLetterStatus ? (
                  <>
                    <LetterAccessCardSkeleton />
                    <LetterAccessCardSkeleton />
                  </>
                ) : (
                  <>
                    <LetterAccessCard title="Offer Letter" href={myLetterStatus.letters.offerLetter?.url} />
                    <LetterAccessCard title="Experience Letter" href={myLetterStatus.letters.experienceLetter?.url} />
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {!isLoading && !error && users.length === 0 && !isHmHrViewOnly && (
          <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-4 py-8 text-center">
            No HM/HR users found under your account.
          </div>
        )}

        {!isLoading && !error && users.length > 0 && !isHmHrViewOnly && (
          <>
            <div className="grid grid-cols-1 gap-6 lg:hidden">
              <div className="min-w-0">
                <div className="overflow-auto border border-gray-200 rounded-lg max-h-[calc(100vh-360px)]">
                  <table className="min-w-full overflow-hidden">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Name</th>
                        <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Role</th>
                        <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Gender</th>
                        <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Email</th>
                        <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Phone</th>
                        <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => {
                        const isSelected = selectedUserId === user.id;
                        const genderLabel = user.gender ?? user.onboarding?.gender ?? "—";
                        return (
                          <tr
                            key={user.id}
                            className={`border-t border-gray-200 cursor-pointer transition-colors ${
                              isSelected ? "bg-orange-50" : "hover:bg-gray-50"
                            }`}
                            onClick={() => {
                              setSelectedUserId(user.id);
                              setIsPreviewOpen(true);
                            }}
                          >
                            <td className="px-4 py-3 text-sm text-gray-800 font-medium">{user.username}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{user.role}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{genderLabel}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{user.email || "-"}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{user.phone || "-"}</td>
                            <td className="px-4 py-3 text-sm">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  user.isBlocked
                                    ? "bg-red-100 text-red-700"
                                    : "bg-green-100 text-green-700"
                                }`}
                              >
                                {user.isBlocked ? "Blocked" : "Active"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="overflow-auto border border-gray-200 rounded-lg max-h-[calc(100vh-360px)]">
                <table className="min-w-full overflow-hidden">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Name</th>
                      <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Role</th>
                      <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Gender</th>
                      <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Email</th>
                      <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Phone</th>
                      <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => {
                      const isSelected = selectedUserId === user.id;
                      const genderLabel = user.gender ?? user.onboarding?.gender ?? "—";
                      return (
                        <tr
                          key={user.id}
                          className={`border-t border-gray-200 cursor-pointer transition-colors ${
                            isSelected ? "bg-orange-50" : "hover:bg-gray-50"
                          }`}
                          onClick={() => {
                            setSelectedUserId(user.id);
                            setIsPreviewOpen(true);
                          }}
                        >
                          <td className="px-4 py-3 text-sm text-gray-800 font-medium">{user.username}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{user.role}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{genderLabel}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{user.email || "-"}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{user.phone || "-"}</td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                user.isBlocked
                                  ? "bg-red-100 text-red-700"
                                  : "bg-green-100 text-green-700"
                              }`}
                            >
                              {user.isBlocked ? "Blocked" : "Active"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
        {!isLoading && !error && !isHmHrViewOnly && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-gray-600">
              Page {page} of {Math.max(totalPages, 1)} · {totalResults} total
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1 || totalResults === 0}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              >
                Previous
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= totalPages || totalResults === 0}
                onClick={() => setPage((prev) => prev + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={isPreviewOpen && !!activeUser} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="w-[95vw] max-w-6xl h-[92vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {selectedLetterType === "experience" ? "Experience Letter Preview" : "Joining Letter Preview"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-3 px-1">
            <p className="text-sm font-medium text-gray-700">Letter type:</p>
            <RadioGroup
              value={selectedLetterType}
              onValueChange={(value: "experience" | "joining") => setSelectedLetterType(value)}
              className="flex items-center gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="experience" id="popup-letter-type-experience" />
                <Label htmlFor="popup-letter-type-experience" className="text-sm text-gray-700 cursor-pointer">
                  Experience Letter
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="joining" id="popup-letter-type-joining" />
                <Label htmlFor="popup-letter-type-joining" className="text-sm text-gray-700 cursor-pointer">
                  Offer Letter
                </Label>
              </div>
            </RadioGroup>
          </div>
          <div className={`flex-1 min-h-0 bg-gray-100 rounded-md p-2 ${selectedLetterType === "joining" && !effectivePreviewUrl ? "overflow-auto" : "overflow-hidden flex items-center justify-center"}`}>
            {effectivePreviewUrl ? (
              <iframe
                src={effectivePreviewUrl}
                title={`${selectedLetterType}-letter-pdf`}
                className="h-full w-full rounded-md bg-white"
              />
            ) : activeUser ? (
              selectedLetterType === "experience" ? (
                <div
                  style={{
                    width: `${794 * docScale}px`,
                    height: `${1123 * docScale}px`,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      transform: `scale(${docScale})`,
                      transformOrigin: "top left",
                      width: "794px",
                      height: "1123px",
                    }}
                  >
                    <Expe
                      date={today}
                      refNo={activeUser?.letters?.experienceLetter?.refNo}
                      candidateName={activeUser.username}
                      designation={experienceDesignationInput || previewDesignation}
                      fromDate={experienceFromDate || undefined}
                      toDate={experienceToDate || undefined}
                      gender={letterGender}
                    />
                  </div>
                </div>
              ) : isHmJoiningContract ? (
                <div className="flex flex-col items-center gap-4 py-2">
                  {[1, 2, 3, 4, 5, 6,7].map((pageNumber) => (
                    <div
                      key={`hm-contract-preview-page-${pageNumber}`}
                      style={{
                        width: `${794 * docScale}px`,
                        height: `${1123 * docScale}px`,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          transform: `scale(${docScale})`,
                          transformOrigin: "top left",
                          width: "794px",
                          height: "1123px",
                        }}
                      >
                        <HmContractLetter
                          date={hmContractDateInput || undefined}
                          candidateName={activeUser.username}
                          address={hmContractAddress || activeUser.location || undefined}
                          pageNumber={pageNumber}
                          gender={letterGender}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center gap-4 py-2">
                  {[1, 2, 3, 4, 5].map((pageNumber) => (
                    <div
                      key={`joining-preview-page-${pageNumber}`}
                      style={{
                        width: `${794 * docScale}px`,
                        height: `${1123 * docScale}px`,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          transform: `scale(${docScale})`,
                          transformOrigin: "top left",
                          width: "794px",
                          height: "1123px",
                        }}
                      >
                        <OfferLetter
                          date={today}
                          refNo={activeUser?.letters?.offerLetter?.refNo}
                          candidateName={activeUser.username}
                          designation={previewDesignation}
                          joiningDate={joiningDateInput || today}
                          pageNumber={pageNumber}
                          gender={letterGender}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : null}
          </div>
          <div className="flex items-center justify-end pt-2">
            <Button type="button" onClick={handleSubmitClick} disabled={!canCreateLetters || !activeUser || isSubmittingLetter}>
              {isSubmittingLetter ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : !canCreateLetters ? (
                "View Only"
              ) : effectivePreviewUrl ? (
                "Regenerate & Save PDF"
              ) : (
                "Submit"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isExperienceDateDialogOpen} onOpenChange={setIsExperienceDateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enter Experience Dates</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="experience-from-date">Start Date</Label>
              <Input
                id="experience-from-date"
                type="date"
                value={experienceFromDate}
                onChange={(e) => {
                  setExperienceFromDate(e.target.value);
                  setExperienceFormErrors((prev) => ({ ...prev, fromDate: undefined }));
                }}
                max={experienceToDate || undefined}
                className={experienceFormErrors.fromDate ? "border-red-500 focus-visible:ring-red-500" : ""}
              />
              {experienceFormErrors.fromDate ? (
                <p className="text-xs text-red-600">{experienceFormErrors.fromDate}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="experience-to-date">End Date</Label>
              <Input
                id="experience-to-date"
                type="date"
                value={experienceToDate}
                onChange={(e) => {
                  setExperienceToDate(e.target.value);
                  setExperienceFormErrors((prev) => ({ ...prev, toDate: undefined }));
                }}
                min={experienceFromDate || undefined}
                className={experienceFormErrors.toDate ? "border-red-500 focus-visible:ring-red-500" : ""}
              />
              {experienceFormErrors.toDate ? (
                <p className="text-xs text-red-600">{experienceFormErrors.toDate}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="experience-designation">Designation</Label>
              <Input
                id="experience-designation"
                value={experienceDesignationInput}
                maxLength={35}
                onChange={(e) => {
                  setExperienceDesignationInput(e.target.value.slice(0, 35));
                  setExperienceFormErrors((prev) => ({ ...prev, designation: undefined }));
                }}
                placeholder="Enter designation"
                className={experienceFormErrors.designation ? "border-red-500 focus-visible:ring-red-500" : ""}
              />
              {experienceFormErrors.designation ? (
                <p className="text-xs text-red-600">{experienceFormErrors.designation}</p>
              ) : null}
              <p className="text-xs text-gray-500">{experienceDesignationInput.length}/35 characters</p>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setIsExperienceDateDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => {
                  const normalizedDesignation = experienceDesignationInput.trim();
                  const nextErrors: {
                    fromDate?: string;
                    toDate?: string;
                    designation?: string;
                  } = {};

                  if (!experienceFromDate) {
                    nextErrors.fromDate = "Start date is required.";
                  }
                  if (!experienceToDate) {
                    nextErrors.toDate = "End date is required.";
                  }
                  if (experienceFromDate && experienceToDate && experienceFromDate > experienceToDate) {
                    nextErrors.toDate = "End date must be on or after start date.";
                  }
                  if (!normalizedDesignation) {
                    nextErrors.designation = "Designation is required.";
                  }

                  setExperienceFormErrors(nextErrors);

                  if (Object.keys(nextErrors).length > 0) {
                    return;
                  }
                  setIsExperienceDateDialogOpen(false);
                  void handleSubmitLetter({
                    fromDate: experienceFromDate,
                    toDate: experienceToDate,
                    designation: normalizedDesignation,
                  });
                }}
              >
                Continue & Submit
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isJoiningDateDialogOpen} onOpenChange={setIsJoiningDateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enter Joining Date</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!isHmJoiningContract ? (
              <div className="space-y-2">
                <Label htmlFor="joining-date">Joining Date</Label>
                <Input
                  id="joining-date"
                  type="date"
                  value={joiningDateInput}
                  onChange={(e) => setJoiningDateInput(e.target.value)}
                />
              </div>
            ) : null}
            {isHmJoiningContract ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="hm-contract-date">Contract Date</Label>
                  <Input
                    id="hm-contract-date"
                    type="date"
                    value={hmContractDateInput}
                    onChange={(e) => setHmContractDateInput(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hm-contract-address">Address</Label>
                  <Input
                    id="hm-contract-address"
                    value={hmContractAddress}
                    onChange={(e) => setHmContractAddress(e.target.value)}
                    placeholder="Enter candidate address"
                  />
                </div>
              </>
            ) : null}
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setIsJoiningDateDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => {
                  if (!isHmJoiningContract && !joiningDateInput) {
                    toast.error("Please select a joining date.");
                    return;
                  }
                  if (isHmJoiningContract && !hmContractDateInput) {
                    toast.error("Please select the contract date.");
                    return;
                  }
                  if (isHmJoiningContract && !hmContractAddress.trim()) {
                    toast.error("Please enter the address.");
                    return;
                  }
                  setIsJoiningDateDialogOpen(false);
                  void handleSubmitLetter({
                    fromDate: "",
                    toDate: "",
                    joiningDate: isHmJoiningContract ? undefined : joiningDateInput,
                    address: hmContractAddress.trim(),
                    contractDate: hmContractDateInput,
                  });
                }}
              >
                Continue & Submit
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OfferExperienceLetter;
